import React, { useState } from 'react';

import {
  Badge,
  Button,
  Divider,
  Flex,
  Paper,
  ScrollArea,
  Space,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title
} from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { MemoryType } from 'shared/interfaces/Memory';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface CreateMemoryPreviewViewProps {
  initialGeneratedMemory: string;
  onGeneratedMemoryChange: (value: string) => void;
  isSaving: boolean;
  onSave: () => void;
  onBack: () => void;
  smartAssistant: SmartAssistant;
  memoryBeingEditedIndex?: number;
  showBackButton: boolean;
}

export function CreateMemoryPreviewView({
  initialGeneratedMemory,
  onGeneratedMemoryChange,
  isSaving,
  onSave,
  onBack,
  smartAssistant,
  memoryBeingEditedIndex,
  showBackButton
}: CreateMemoryPreviewViewProps) {
  const { t } = useTranslation();
  const [memoryTestMessage, setMemoryTestMessage] = useState<string>('');
  const [memoryTestResponse, setMemoryTestResponse] = useState<string>('');
  const [memoryTestIsLoading, setMemoryTestIsLoading] = useState(false);

  const askSmartAssistantSyncMutation = useMutation({
    mutationFn: SmartAssistantService.askSmartAssistantSync,
    onSuccess: (response) => {
      setMemoryTestResponse(response.text);
    },
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.MEMORY.FAILURE.TEST');
    },
    onSettled: () => {
      setMemoryTestIsLoading(false);
    }
  });

  const handleTestMemory = async () => {
    const smartAssistantId = smartAssistant._id;
    if (!smartAssistantId || !initialGeneratedMemory || !memoryTestMessage) return;

    setMemoryTestIsLoading(true);
    setMemoryTestResponse('');

    // If we're editing a memory, we need to exclude it from the existing memories to avoid duplicating it when testing
    const existingMemories =
      memoryBeingEditedIndex !== undefined && smartAssistant.memories
        ? smartAssistant.memories.filter((_, index) => index !== memoryBeingEditedIndex)
        : smartAssistant.memories || [];

    const overrideMemories = [
      ...existingMemories,
      // Add the memory being tested to the memories
      {
        type: MemoryType.GENERIC,
        genericMemoryContent: {
          content: initialGeneratedMemory
        }
      }
    ];

    // Initiate the ask mutation
    askSmartAssistantSyncMutation.mutate({
      smartAssistantId,
      conversationInfo: {
        messages: [
          {
            fromUser: true,
            text: memoryTestMessage
          }
        ]
      },
      overrideMemories
    });
  };

  return (
    <Stack spacing={16}>
      <Stack spacing={4}>
        <Title order={4}>{t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.PREVIEW_VIEW.GENERATED_MEMORY')}</Title>
        <Textarea
          minRows={10}
          maxRows={10}
          value={initialGeneratedMemory}
          onChange={(event) => onGeneratedMemoryChange(event.target.value)}
        />
      </Stack>

      <Divider />

      <Stack spacing={4}>
        <Flex gap={8} align="center">
          <Title order={4}>{t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.PREVIEW_VIEW.TEST_YOUR_MEMORY')}</Title>
          <Badge color="gray" size="sm" variant="light" tt="capitalize">
            {t('APP.ACTIONS.OPTIONAL')}
          </Badge>
        </Flex>

        <Flex gap={12} w="100%">
          <TextInput
            placeholder={t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.PREVIEW_VIEW.TEST_YOUR_MEMORY_PLACEHOLDER')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleTestMemory();
              }
            }}
            w="100%"
            value={memoryTestMessage}
            onChange={(event) => setMemoryTestMessage(event.target.value)}
          />
          <Button
            onClick={handleTestMemory}
            variant="default"
            loading={memoryTestIsLoading}
            disabled={!memoryTestMessage}
            rightIcon={<IconPlayerPlay style={{ width: 16, height: 16 }} />}>
            {t('APP.ACTIONS.TEST')}
          </Button>
        </Flex>
      </Stack>

      {memoryTestResponse && (
        <Paper withBorder>
          <ScrollArea.Autosize mah={200} p="xs">
            <Text py={4}>
              <Text span weight={700}>
                {t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.PREVIEW_VIEW.AGENT_RESPONSE')}
              </Text>
              <Space h={4} />
              <Text>{memoryTestResponse}</Text>
            </Text>
          </ScrollArea.Autosize>
        </Paper>
      )}

      <Flex justify="flex-end">
        <Flex justify="space-between" gap={16}>
          {showBackButton && (
            <Button onClick={onBack} variant="default">
              {t('APP.ACTIONS.BACK')}
            </Button>
          )}
          <Button onClick={onSave} loading={isSaving} disabled={!initialGeneratedMemory}>
            {t('SMART_ASSISTANT.MEMORIES_SECTION.MODAL.PREVIEW_VIEW.SAVE_MEMORY')}
          </Button>
        </Flex>
      </Flex>
    </Stack>
  );
}
